import { useCallback, useMemo, useRef, useState } from "react";
import {
  createEditor,
  BaseEditor,
  Descendant,
  Transforms,
  Element as SlateElement,
  Text as SlateText,
} from "slate";
import { Slate, Editable, withReact, ReactEditor } from "slate-react";
import { Space, Popover, Tag, Button } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import TextStyleSelector from "./TextStyleSelector";
import { ConfigV2 } from "../DynamicThumbnailForm";

type CustomElement = {
  type: "paragraph" | "tag";
  children: (CustomText | CustomElement)[];
};

type CustomText = { text: string };

declare module "slate" {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

interface InputWithTagsProps {
  previewText: string;
  displayText: string;
  config: ConfigV2;
  setConfig: (config: ConfigV2) => void;
  textIndex: number;
  previewTextArr: Array<string | { id: string; title?: string }>;
  unlockedUpTo: number;
}

const InputWithTags = ({
  displayText,
  config,
  setConfig,
  textIndex,
  previewTextArr,
  unlockedUpTo,
}: InputWithTagsProps) => {
  const [editorValue, setEditorValue] = useState<Descendant[]>(
    convertArrToDescendant(previewTextArr)
  );
  const inputRef = useRef<Descendant[]>([]);

  function isCustomElement(node: Descendant): node is CustomElement {
    return (
      !SlateText.isText(node) &&
      (node as CustomElement).children !== undefined &&
      (node as CustomElement).type !== undefined
    );
  }
  function isCustomText(node: Descendant): node is CustomText {
    return SlateText.isText(node);
  }

  function convertArrToDescendant(
    arr: (string | { id: string; title?: string })[]
  ): Descendant[] {
    const children = arr.map((item) => {
      if (typeof item === "string") {
        return { text: item } as CustomText;
      }
      if (item?.id) {
        return {
          type: "tag",
          children: [{ text: item.id }],
        } as CustomElement;
      }
      return { text: "" } as CustomText;
    });

    return [
      {
        type: "paragraph",
        children,
      } as CustomElement,
    ];
  }

  const editor = useMemo(() => {
    const edt = withReact(createEditor());
    const { isInline, isVoid } = edt;

    edt.isInline = (element) => {
      return element.type === "tag" ? true : isInline(element);
    };
    edt.isVoid = (element) => {
      return element.type === "tag" ? true : isVoid(element);
    };

    return edt;
  }, []);

  const renderElement = useCallback((props: any) => {
    switch (props.element.type) {
      case "tag":
        return <TagComponent {...props} />;
      default:
        return <DefaultElement {...props} />;
    }
  }, []);

  const handleInsertParam = (id: string) => {
    const tagNode: CustomElement = {
      type: "tag",
      children: [{ text: `param${id}` }],
    };
    Transforms.insertNodes(editor, [tagNode]);
    Transforms.move(editor);
  };

  const handleEditorChange = (newValue: Descendant[]) => {
    setEditorValue(newValue);

    const formattedArr: Array<string | { id: string; title: string }> = [];

    for (let i = 0; i < newValue.length; i++) {
      const node = newValue[i];
      if (isCustomElement(node)) {
        if (i > 0) {
          formattedArr.push("\n");
        }

        if (inputRef.current !== node.children) {
          const intermArr = node.children.map((child) => {
            if (isCustomText(child)) {
              return child.text;
            } else if (isCustomElement(child) && child.children.length > 0) {
              const maybeText = child.children[0];
              if (isCustomText(maybeText)) {
                return { id: maybeText.text, title: maybeText.text };
              }
            }
            return "";
          });

          formattedArr.push(...intermArr);
        }
      }
    }

    inputRef.current = newValue;
    const nextState: ConfigV2 = JSON.parse(JSON.stringify(config));
    nextState.texts[textIndex].textTemplate = formattedArr;
    setConfig(nextState);
  };

  const paramButtons = Array.from({ length: 10 }, (_, idx) => {
    const paramNumber = idx + 1;
    return (
      <Button
        key={paramNumber}
        disabled={paramNumber > unlockedUpTo}
        onClick={() => handleInsertParam(paramNumber.toString())}
        style={{
          padding: "4px 8px",
          cursor: paramNumber > unlockedUpTo ? "not-allowed" : "pointer",
        }}
      >
        {`Param ${paramNumber}`}
      </Button>
    );
  });

  return (
    <Slate
      editor={editor}
      initialValue={editorValue}
      onChange={handleEditorChange}
    >
      <div className="slateContainer">
        <div className="aboveEditorContainer">
          <p className="textAboveEditor">{displayText}</p>
        </div>

        <Space>
          <div className="buttom-params">{paramButtons}</div>
        </Space>

        <div style={{ display: "flex" }}>
          <Editable
            renderElement={renderElement}
            className="textEditor"
            disableDefaultStyles
            placeholder="Customize this text"
          />
          <Popover
            className="tooltip"
            placement="right"
            overlayInnerStyle={{ width: "250px" }}
            style={{ zIndex: "0" }}
            getPopupContainer={(trigger) =>
              trigger?.parentElement || document.body
            }
            content={
              <div className="extra-info-popover">
                Overlay this text on the image: Use <b>Param1</b>, <b>Param2</b>{" "}
                as parameters.
              </div>
            }
          >
            <div>
              <QuestionCircleOutlined
                style={{ color: "var(--blings_icon_gray)", cursor: "pointer" }}
              />
            </div>
          </Popover>
        </div>

        <TextStyleSelector
          config={config}
          setConfig={setConfig}
          textIndex={textIndex}
        />
      </div>
    </Slate>
  );
};

const TagComponent = (props: any) => {
  const text = props.element.children.map((child: any) => child.text).join("");
  const properText = text.charAt(0).toUpperCase() + text.slice(1);
  return (
    <span
      contentEditable={false}
      {...props.attributes}
      style={{ display: "inline-block", margin: "0 4px" }}
    >
      <Tag style={{ fontSize: "1rem", margin: 0 }}>
        <span style={{ color: "red" }}>{properText}</span>
      </Tag>
      {props.children}
    </span>
  );
};

const DefaultElement = (props: any) => {
  return (
    <p
      style={{
        whiteSpace: "pre-wrap",
        marginTop: ".5em",
        marginBottom: ".5em",
      }}
      {...props.attributes}
    >
      {props.children}
    </p>
  );
};

export default InputWithTags;
